import { useClerk } from '@clerk/clerk-react';
import SaveStatusIndicator from '@src/components/common/SaveStatusIndicator';
import ToastNotification from '@src/components/common/ToastNotification';
import { PublishProgressDrawer } from '@src/components/content-components/PublishProgressDrawer';
import PageVisibilityChangeListener from '@src/components/PageVisibilityChangeListener';
import UserStoryUploadFlow from '@src/components/UserStoryUploadFlow';
import { useAccessControl } from '@src/hooks/useAccessControl';
import { useSidebarState } from '@src/hooks/useSidebarState';
import { useKaraokeStore } from '@src/stores-v2';
import { normalizeLangKey } from '@src/utility/bcp47';
import {
  isOriginalLang,
  ORIGINAL_LANGUAGE,
} from '@src/utility/captionLanguage';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import GlobalQuerySubscriptionComponent from '../components/GlobalQuerySubscriptionComponent';
import MainMenu from '../components/MainMenu';
import { SIDEBAR_SPRING } from '../components/sidebar/sidebarMotion';
import { Studio } from '../types.ts/general';
import { getCurrentStudio } from '../utility/general';
import { Insights } from '../utility/insights';

const AUTH_BOUNCE_STORAGE_KEY = 'studio-auth-redirect-bounces';
const AUTH_BOUNCE_WINDOW_MS = 60_000;
const MAX_AUTH_BOUNCES = 2;

// Counts full-page access-denied redirects to the backend app within a short
// window (sessionStorage survives the round-trip in the same tab). If the
// backend keeps sending the user straight back, a further redirect would
// loop forever — the caller stops and shows a message instead.
const recordExternalRedirect = (): number => {
  let entry = { count: 0, firstAt: Date.now() };
  try {
    const parsed = JSON.parse(
      sessionStorage.getItem(AUTH_BOUNCE_STORAGE_KEY) || '',
    );
    if (
      typeof parsed?.count === 'number' &&
      typeof parsed?.firstAt === 'number' &&
      Date.now() - parsed.firstAt < AUTH_BOUNCE_WINDOW_MS
    ) {
      entry = parsed;
    }
  } catch {
    // missing or malformed entry — start a fresh window
  }
  entry.count += 1;
  try {
    sessionStorage.setItem(AUTH_BOUNCE_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // storage unavailable — let the redirect proceed rather than block
  }
  return entry.count;
};

const clearRedirectBounces = () => {
  try {
    sessionStorage.removeItem(AUTH_BOUNCE_STORAGE_KEY);
  } catch {
    // storage unavailable — nothing to clear
  }
};

const RootLayout = ({ children }: { children: React.ReactNode }) => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const { storyId, showcase } = Object.fromEntries(urlSearchParams.entries());
  const { checkUserAccess } = useAccessControl();
  const clerk = useClerk();
  const [redirectLoopDetected, setRedirectLoopDetected] = useState(false);
  const { sidebarWidth } = useSidebarState();
  const { pathname } = useLocation();
  const currentStudio = getCurrentStudio(pathname);

  // PLAT-10788 (DS8) — the `?lang=` URL param owns the active caption language;
  // seed it into the KaraokeStore here (the app shell, inside the Router) so the
  // domain stores never read the URL. Absent/original → the `ORIGINAL_LANGUAGE`
  // sentinel, which `activeKey` resolves to the source key — so re-firing on a
  // switch-to-original is harmless (same key as the switch's source-key form),
  // not a null-clobber that would disable the write-back.
  const karaokeStore = useKaraokeStore();
  const [searchParams] = useSearchParams();
  const langParam = searchParams.get('lang');
  useEffect(() => {
    karaokeStore.setActiveLanguage(
      langParam && !isOriginalLang(langParam)
        ? normalizeLangKey(langParam)
        : ORIGINAL_LANGUAGE,
    );
  }, [karaokeStore, langParam]);

  useEffect(() => {
    const checkAccess = async () => {
      const result = await checkUserAccess(
        storyId || null,
        showcase,
        currentStudio === Studio.home,
      );
      if (!result.hasAccess && result.redirectTo) {
        const isExternal = result.redirectTo.startsWith('http');
        if (isExternal && recordExternalRedirect() > MAX_AUTH_BOUNCES) {
          console.error(
            'Redirect loop detected — not redirecting to:',
            result.redirectTo,
          );
          setRedirectLoopDetected(true);
          return;
        }
        console.log('Redirecting to:', result.redirectTo);
        window.location.href = result.redirectTo;
      } else if (result.hasAccess) {
        clearRedirectBounces();
      }
    };

    checkAccess();
  }, [storyId, showcase, checkUserAccess, currentStudio]);

  // User-initiated only: an automatic sign-out + redirect here would re-arm
  // the very loop the circuit breaker stopped (SAML SSO can complete a
  // sign-in with zero user input, landing the same denied account back here).
  const handleSignInAgain = async () => {
    clearRedirectBounces();
    try {
      await clerk.signOut();
    } catch (error) {
      console.error('Sign-out failed, navigating to sign-in anyway:', error);
    }
    window.location.href = '/sign-in';
  };

  if (redirectLoopDetected) {
    return (
      <AppShell className="editor">
        <NoAccessScreen>
          <h2>We couldn&apos;t verify your access</h2>
          <p>
            Your account doesn&apos;t have access to this content. Try signing
            in with a different account, or contact your organization admin.
          </p>
          <SignInAgainButton onClick={handleSignInAgain}>
            Sign In with a Different Account
          </SignInAgainButton>
        </NoAccessScreen>
      </AppShell>
    );
  }

  return (
    <AppShell className="editor">
      <Insights />
      <MainMenu />
      <GlobalQuerySubscriptionComponent />
      <UserStoryUploadFlow />
      <PageVisibilityChangeListener />
      <SaveStatusIndicator />
      <PublishProgressDrawer />
      <ToastNotification />
      <MainContent
        animate={{ marginLeft: sidebarWidth }}
        transition={SIDEBAR_SPRING}
        initial={false}
      >
        {children}
      </MainContent>
    </AppShell>
  );
};

export default RootLayout;

const AppShell = styled.div`
  display: flex;
  min-height: 100vh;
`;

/* Animate margin-left via Framer Motion using SIDEBAR_SPRING so it stays
 * perfectly in sync with the sidebar width animation. */
const MainContent = styled(motion.div)`
  flex: 1;
  min-width: 0;
  min-height: 100vh;
  overflow-x: clip;
`;

const NoAccessScreen = styled.div`
  flex: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  background-color: var(--background);

  h2 {
    font-size: var(--text-md);
    font-weight: var(--font-weight-semi-bold);
    color: var(--foreground);
  }

  p {
    max-width: 420px;
    font-size: var(--text-sm);
    color: var(--text-secondary);
  }
`;

const SignInAgainButton = styled.button`
  height: var(--btn-h-md);
  padding: 0 var(--btn-px-md);
  border: none;
  border-radius: var(--radius-button);
  background: var(--primary);
  color: var(--background);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  cursor: pointer;

  &:hover {
    background: var(--primary-hover);
  }
`;
